import currency from 'currency.js';
import _, { omit, uniq, differenceWith, isEqual, reverse } from 'lodash';
import { v4 as uuid } from 'uuid';

import { calculateNet, convertWithExchangeRate } from 'selectors/periods';
import { SortingOrder } from 'constants/grid';
import {
  arrToObj,
  filterProperties,
  removeProperty,
  zip,
  getArrayDuplicateValues,
} from 'utils/generic';

/* ------------------------------- Client ---------------------------------- */

export const editClient = (stateClient, client) => {
  return {
    ...stateClient,
    ...client,
  };
};

/* ------------------------------ Accounts -------------------------------- */

export const assignAccount = (stateAccounts, accountId, groupId) => {
  return {
    ...stateAccounts,
    accountsById: {
      ...stateAccounts.accountsById,
      [accountId]: {
        ...stateAccounts.accountsById[accountId],
        groupId: groupId,
      },
    },
  };
};

export const assignAccounts = (stateAccounts, accountIds, groupId) => {
  return {
    ...stateAccounts,
    accountsById: Object.entries(stateAccounts.accountsById).reduce(
      (accountsById, [accountId, account]) => ({
        ...accountsById,
        [accountId]: {
          ...account,
          groupId: accountIds.includes(parseInt(accountId, 10))
            ? groupId
            : account.groupId,
        },
      }),
      {}
    ),
  };
};

export const sortAccounts = (stateAccounts, field, rows, sortingOrder) => {
  const { accountIds } = stateAccounts;
  const sortedAccountIds = [...accountIds].sort((accountIdA, accountIdB) => {
    const accountA = rows[accountIdA];
    const accountB = rows[accountIdB];

    if (!(accountA && accountB)) {
      return 0;
    }

    let difference = 0;

    const [columnLabel, periodId] = field.split('-');
    if (columnLabel === 'net') {
      const accountNetA = calculateNet(accountA, periodId);
      const accountNetB = calculateNet(accountB, periodId);
      difference = accountNetA - accountNetB;
    } else {
      difference = accountA[field] - accountB[field];
    }

    const sign = sortingOrder === SortingOrder.ASC ? 1 : -1;
    return sign * difference;
  });

  return {
    ...stateAccounts,
    sortedAccountIds,
  };
};

const createAccount = (data, mapping) => {
  return Object.entries(mapping).reduce(
    (target, source) => {
      const [key, colId] = source;
      if (key.startsWith('period')) {
        target.periods[key] = {
          unadjusted: currency(colId >= 0 ? data[colId] : 0),
        };
      } else {
        target[key] = colId >= 0 ? data[colId] : '';
      }
      return target;
    },
    { groupId: null, periods: {} }
  );
};

/*
 * Add account below account with accountId
 */
export const addAccount = (stateAccounts, accountId) => {
  const index =
    stateAccounts.accountIds.findIndex((el) => el === accountId) + 1;
  const newAccountId = getAvailableIds(stateAccounts.accountIds, 1)[0];
  const accountMapping = [
    'number',
    'description',
    ...stateAccounts.periodIds,
  ].reduce((t, s) => {
    t[s] = -1;
    return t;
  }, {});
  return {
    ...stateAccounts,
    accountsById: {
      ...stateAccounts.accountsById,
      [newAccountId]: createAccount([], accountMapping),
    },
    accountIds: [
      ...stateAccounts.accountIds.slice(0, index),
      newAccountId,
      ...stateAccounts.accountIds.slice(index),
    ],
  };
};

/*
 * Updates multiple existing accounts from data argument with given mapping.
 */
export const updateAccounts = (stateAccounts, data, mapping) => {
  let accountsByNumDes = {};
  for (let [accountId, account] of Object.entries(stateAccounts.accountsById)) {
    let numDesKey = `${account.number},${account.description}`;
    // multiple accounts with same numDesKey are currently allowed
    if (!accountsByNumDes.hasOwnProperty(numDesKey)) {
      accountsByNumDes[numDesKey] = [];
    }
    accountsByNumDes[numDesKey].push([accountId, account]);
  }
  let updatedAccounts = {};
  const periodMapping = Object.entries(mapping).filter(
    ([key, colId]) => key.startsWith('period') && colId >= 0
  );
  for (let row of data) {
    let numDesKey = `${row[mapping.number]},${row[mapping.description]}`;
    for (let [accountId, account] of accountsByNumDes[numDesKey]) {
      updatedAccounts[accountId] = {
        ...account,
        periods: {
          ...account.periods,
          ...periodMapping.reduce((target, source) => {
            const [key, colId] = source;
            return {
              ...target,
              [key]: {
                unadjusted: currency(row[colId]),
              },
            };
          }, {}),
        },
      };
    }
  }
  return {
    ...stateAccounts,
    accountsById: {
      ...stateAccounts.accountsById,
      ...updatedAccounts,
    },
  };
};

/*
 * Updates multiple existing accounts from data argument with given mapping,
 * when Number is same but Description is not.
 */
export const updateAccountsWithSameNumber = (stateAccounts, data, mapping) => {
  const accountsByNum = {};
  for (const [accountId, account] of Object.entries(
    stateAccounts.accountsById
  )) {
    const numKey = account.number;
    // update only the first account with the given account number
    if (!accountsByNum.hasOwnProperty(numKey)) {
      accountsByNum[numKey] = [[accountId, account]];
    }
  }
  const updatedAccounts = {};
  const periodMapping = Object.entries(mapping).filter(
    ([key, colId]) => key.startsWith('period') && colId >= 0
  );

  for (const row of data) {
    const numCol = mapping.number;
    const desCol = mapping.description;

    const numKey = row[numCol];

    const hasDesCol = !(desCol < 0 || !row[desCol]);

    for (const [accountId, account] of accountsByNum[numKey]) {
      const hasNewDescription = hasDesCol && !!row[desCol];

      const updatedDescription = hasNewDescription
        ? row[desCol]
        : account.description;

      updatedAccounts[accountId] = {
        ...account,
        description: updatedDescription,
        periods: {
          ...account.periods,
          ...periodMapping.reduce((target, source) => {
            const [key, colId] = source;
            return {
              ...target,
              [key]: {
                unadjusted: currency(row[colId]),
              },
            };
          }, {}),
        },
      };
    }
  }
  return {
    ...stateAccounts,
    accountsById: {
      ...stateAccounts.accountsById,
      ...updatedAccounts,
    },
  };
};

/*
 * Creates multiple new accounts from data argument with given mapping.
 */
export const addAccounts = (stateAccounts, data, mapping) => {
  const availableIds = getAvailableIds(stateAccounts.accountIds, data.length);
  const newAccounts = zip([data, availableIds])
    .map(([row, id]) => [createAccount(row, mapping), id])
    .filter(([acc, id]) => acc.number || acc.description)
    .reduce((target, [source, id]) => {
      target[id] = source;
      return target;
    }, {});
  return {
    ...stateAccounts,
    accountsById: {
      ...stateAccounts.accountsById,
      ...newAccounts,
    },
    accountIds: [
      ...stateAccounts.accountIds,
      ...Object.keys(newAccounts).map((id) => parseInt(id, 10)),
    ],
  };
};

/*
 * Compare given account data and mapping with current state and split it to:
 *
 * 1. data to be updated - rows with pair (number, description) that
 *    already exists in the state
 * 2. data to be added - other rows in data with nonempty number or description
 *
 * Additionally return number of rows in data to be added that have same number
 * or description as existing accounts in the state
 */
export const prepareImportAccounts = (stateAccounts, data, mapping) => {
  let accountNum = new Set();
  let accountDes = new Set();
  let accountNumDes = new Set();
  for (const account of Object.values(stateAccounts.accountsById)) {
    accountNum.add(account.number);
    accountDes.add(account.description);
    accountNumDes.add(`${account.number},${account.description}`);
  }

  const numCol = mapping.number;
  const desCol = mapping.description;
  let dupNum = 0;
  let dupDes = 0;
  let dataToUpdate = [];
  let dataToAdd = [];
  for (const row of data) {
    if ((numCol < 0 || !row[numCol]) && (desCol < 0 || !row[desCol])) continue;
    if (
      numCol >= 0 &&
      desCol >= 0 &&
      accountNumDes.has(`${row[numCol]},${row[desCol]}`)
    ) {
      dataToUpdate.push(row);
    } else {
      dataToAdd.push(row);
      if (numCol >= 0 && accountNum.has(row[numCol])) ++dupNum;
      if (desCol >= 0 && accountDes.has(row[desCol])) ++dupDes;
    }
  }
  return [dataToUpdate, dataToAdd, dupNum, dupDes];
};

export const getDuplicatedNumbersInImportData = (data, mapping) => {
  const accountNums = getAccountNumsInImportData(data, mapping);
  return uniq(getArrayDuplicateValues(accountNums));
};

export const getDuplicatedNumbersInAccountsAndImportData = (
  stateAccounts,
  data,
  mapping
) => {
  const duplicatedAccountNums = [];
  const duplicatedAccountData = [];

  const accountNums = new Set();
  const accountNumDes = new Set();

  for (const account of Object.values(stateAccounts.accountsById)) {
    accountNums.add(account.number);
    accountNumDes.add(`${account.number},${account.description}`);
  }

  const numCol = mapping.number;
  const desCol = mapping.description;

  for (const row of data) {
    const hasNumCol = !(numCol < 0 || !row[numCol]);
    const hasDesCol = !(desCol < 0 || !row[desCol]);

    const accountNumberToBeImported = hasNumCol ? row[numCol] : '';
    const accountDescriptionToBeImported = hasDesCol ? row[desCol] : '';

    const hasAccountNum = accountNums.has(accountNumberToBeImported);
    const hasAccountNumAndDescription = accountNumDes.has(
      `${accountNumberToBeImported},${accountDescriptionToBeImported}`
    );
    const hasOnlyAccountNum = hasAccountNum && !hasAccountNumAndDescription;

    if (hasOnlyAccountNum) {
      duplicatedAccountNums.push(accountNumberToBeImported);
      duplicatedAccountData.push(row);
    }
  }

  return [uniq(duplicatedAccountNums), duplicatedAccountData];
};

const getAccountNumsInImportData = (data, mapping) => {
  const accountNums = [];
  const numCol = mapping.number;

  for (const row of data) {
    const hasNumCol = !(numCol < 0 || !row[numCol]);
    if (hasNumCol) {
      const accountNumber = row[numCol];
      accountNums.push(accountNumber);
    }
  }

  return accountNums;
};

export const importAccounts = (stateAccounts, data, mapping) => {
  const [dataToUpdate, dataToAdd] = prepareImportAccounts(
    stateAccounts,
    data,
    mapping
  );

  const [, duplicatedAccountData] = getDuplicatedNumbersInAccountsAndImportData(
    stateAccounts,
    data,
    mapping
  );

  const finalDataToAdd = getFinalDataToAdd({
    dataToAdd,
    duplicatedAccountData,
  });

  let newStateAccounts = updateAccounts(stateAccounts, dataToUpdate, mapping);
  newStateAccounts = updateAccountsWithSameNumber(
    newStateAccounts,
    duplicatedAccountData,
    mapping
  );
  newStateAccounts = addAccounts(newStateAccounts, finalDataToAdd, mapping);

  return newStateAccounts;
};

export const getFinalDataToAdd = ({ dataToAdd, duplicatedAccountData }) =>
  differenceWith(dataToAdd, duplicatedAccountData, isEqual);

export const importAccountsWithAdding = (stateAccounts, data, mapping) => {
  const [dataToUpdate, dataToAdd] = prepareImportAccounts(
    stateAccounts,
    data,
    mapping
  );

  let newStateAccounts = updateAccounts(stateAccounts, dataToUpdate, mapping);
  newStateAccounts = addAccounts(newStateAccounts, dataToAdd, mapping);

  return newStateAccounts;
};

/*
 * Removes account with given accountId and all journal entry items related
 * to this account.
 */
export const removeAccount = (state, accountId) => {
  let entryItemIdsToRemove = new Set(
    Object.entries(state.journal.entryItemsById)
      .filter(
        ([itemId, item]) =>
          item.contentType === 'account' && item.contentId === accountId
      )
      .map(([itemId, item]) => itemId)
  );
  let journal = removeJournalEntryItems(state.journal, entryItemIdsToRemove);
  return {
    ...state,
    accounts: {
      ...state.accounts,
      accountsById: removeProperty(state.accounts.accountsById, accountId),
      accountIds: state.accounts.accountIds.filter((acc) => acc !== accountId),
    },
    journal,
  };
};

/* ------------------------ Chart of accounts ----------------------------- */

/*
 * For given caString (number and description in each line) return caItems map
 * and array of duplicate numbers. For each duplicate number only last
 * description is retained in caItems map.
 */
export const caItemsFromString = (caString) => {
  const re = /^(\d+) (.*)/;
  let caItems = new Map();
  let duplicates = [];
  for (const line of caString.split('\n')) {
    let mo = line.match(re);
    if (mo === null) continue;
    let [number, description] = mo.slice(1);
    if (caItems.has(number)) {
      duplicates.push(number);
    }
    caItems.set(number, { description });
  }
  for (let [caNumber, caItem] of caItems) {
    // use caNumber slice to avoid setting parent to itself
    caItem.parent = getByPrefixRule(caItems, caNumber.slice(0, -1));
  }
  return [caItems, duplicates];
};

/*
 * Use prefix rule to get caItem number with longest common prefix.
 */
export const getByPrefixRule = (caItems, caNumber) => {
  let candidate = caNumber;
  while (candidate.length > 0 && !caItems.has(candidate)) {
    candidate = candidate.slice(0, -1);
  }
  return candidate;
};

export const mapAccountsToGroups = (state) => {
  const groupIdByGroupNumber = {};
  Object.keys(state.groups.groupsById).forEach((groupId) => {
    const group = state.groups.groupsById[groupId];
    if (group && group.number) {
      groupIdByGroupNumber[group.number] = groupId;
    }
  });

  const groupNumbers = new Set(Object.keys(groupIdByGroupNumber));
  const accountsById = {};
  Object.keys(state.accounts.accountsById).forEach((accountId) => {
    const account = state.accounts.accountsById[accountId];
    accountsById[accountId] = { ...account };
    const parentGroupNumber = getByPrefixRule(
      groupNumbers,
      account.number.toString()
    );
    if (parentGroupNumber in groupIdByGroupNumber) {
      accountsById[accountId].groupId = parseInt(
        groupIdByGroupNumber[parentGroupNumber],
        10
      );
    }
  });

  return {
    ...state,
    accounts: {
      ...state.accounts,
      accountsById: {
        ...state.accounts.accountsById,
        ...accountsById,
      },
    },
  };
};

/*
 * Assign all accounts that can be assigned to the chart of accounts (caItems)
 *   - remove all existing groups
 *   - create all groups or only used groups and their ancestors
 *   - for each account use prefix rule to assign to created group
 */
export const mapAccounts = (state, caItems, usedGroupsOnly = false) => {
  // first remove all groups
  let newState = removeGroup(state, 0);

  // find out what groups to create using account numbers caItems
  let caItemNumToCreateGroups = new Set();
  let accountsToMap = new Map();
  for (const [accountId, account] of Object.entries(
    newState.accounts.accountsById
  )) {
    let caItemNum = getByPrefixRule(caItems, account.number);
    if (caItemNum !== '') {
      // if caItemNum is not empty there is another account to map
      accountsToMap.set(accountId, caItemNum);
      // add all ancestor caItems in caItemNumToCreateGroups
      do {
        caItemNumToCreateGroups.add(caItemNum);
        caItemNum = caItems.get(caItemNum).parent;
      } while (caItemNum !== '');
    }
  }
  // if required create all groups from chart of accounts, not only used groups
  if (!usedGroupsOnly) {
    caItemNumToCreateGroups = new Set(caItems.keys());
  }

  // bulk create all groups in groups object
  let groups = {
    ...newState.groups,
    groupsById: {
      ...newState.groups.groupsById,
    },
  };
  let groupIdByCaItemNum = new Map();
  groupIdByCaItemNum.set('', 0);
  // in order to create parent groups first sort by caItem number length and then lexicographically
  const caItemNumToCreateGroupsSorted = Array.from(
    caItemNumToCreateGroups
  ).sort((a, b) =>
    a.length !== b.length ? a.length - b.length : a.localeCompare(b)
  );
  for (const caItemNum of caItemNumToCreateGroupsSorted) {
    const caItem = caItems.get(caItemNum);
    let groupId = ++groups.groupIdMax;
    groups.groupsById[groupId] = {
      name: caItemNum + ' ' + caItems.get(caItemNum).description,
      childrenIds: [],
      code: '',
      number: '',
      cgroupId: null,
      expanded: true,
    };
    groupIdByCaItemNum.set(caItemNum, groupId);
    groups.groupsById[groupIdByCaItemNum.get(caItem.parent)].childrenIds.push(
      groupId
    );
  }

  // bulk assign accounts to groups in accountsById object
  let accountsById = {};
  for (const [accountId, caItemNum] of accountsToMap) {
    accountsById[accountId] = { ...newState.accounts.accountsById[accountId] };
    accountsById[accountId].groupId = groupIdByCaItemNum.get(caItemNum);
  }

  return {
    ...newState,
    accounts: {
      ...newState.accounts,
      accountsById: {
        ...newState.accounts.accountsById,
        ...accountsById,
      },
    },
    groups,
  };
};

/* ----------------------------- Periods ---------------------------------- */

export const addPeriod = (state, name, numberOfDays) => {
  const period = { name, numberOfDays };
  const periodId = `period${state.accounts.periodIdMax + 1}`;
  // insert default values for unadjusted and adjusted for all accounts
  const accountsById = Object.entries(state.accounts.accountsById).reduce(
    (prev, [accountId, account]) => ({
      ...prev,
      [accountId]: {
        ...account,
        periods: {
          ...account.periods,
          [periodId]: {
            unadjusted: 0,
            adjusted: 0,
          },
        },
      },
    }),
    {}
  );
  const settings = {
    ...state.settings,
    exchangeRates: (state?.settings?.exchangeRates || []).map(
      (exchangeRate) => {
        return {
          ...exchangeRate,
          [`er-${periodId}`]: DEFAULT_EXCHANGE_RATE,
        };
      }
    ),
  };
  return {
    ...state,
    accounts: {
      ...state.accounts,
      accountsById,
      periodIdMax: state.accounts.periodIdMax + 1,
      periodsById: {
        ...state.accounts.periodsById,
        [periodId]: period,
      },
      periodIds: [...state.accounts.periodIds, periodId],
    },
    consolidations: {
      ...state.consolidations,
      subsidiariesById: Object.entries(
        state.consolidations.subsidiariesById
      ).reduce(
        (prev, [subId, sub]) => ({
          ...prev,
          [subId]: {
            ...sub,
            periodsMap: {
              ...sub.periodsMap,
              [periodId]: null,
            },
          },
        }),
        {}
      ),
      cgroupsById: Object.entries(state.consolidations.cgroupsById).reduce(
        (prev, [cgroupId, cgroup]) => ({
          ...prev,
          [cgroupId]: {
            ...cgroup,
            cfPeriods: {
              ...cgroup.cfPeriods,
              [periodId]: currency(0),
            },
          },
        }),
        {}
      ),
    },
    settings,
  };
};

/*
 * Remove period and all journal entries belonging to removed period.
 * Remove period form all accounts, subsidiaries and cgroups.
 */
export const removePeriod = (state, periodId) => {
  // remove period from all accounts
  const accountsById = Object.entries(state.accounts.accountsById).reduce(
    (prev, [accountId, account]) => ({
      ...prev,
      [accountId]: {
        ...account,
        periods: removeProperty(account.periods, periodId),
      },
    }),
    {}
  );
  // remove all journal entries belonging to period with given periodId
  let entryIds = new Set(
    Object.entries(state.journal.entriesById)
      .filter(([id, entry]) => entry.periodId === periodId)
      .map(([id, entry]) => parseInt(id, 10))
  );
  let journal = removeJournalEntries(state.journal, entryIds);
  const settings = {
    ...state.settings,
    exchangeRates: (state?.settings?.exchangeRates || []).map(
      (exchangeRate) => {
        return removeProperty(exchangeRate, `er-${periodId}`);
      }
    ),
  };
  return {
    ...state,
    journal,
    accounts: {
      ...state.accounts,
      accountsById,
      periodsById: removeProperty(state.accounts.periodsById, periodId),
      periodIds: state.accounts.periodIds.filter((p) => p !== periodId),
    },
    consolidations: {
      ...state.consolidations,
      subsidiariesById: Object.entries(
        state.consolidations.subsidiariesById
      ).reduce(
        (prev, [subId, sub]) => ({
          ...prev,
          [subId]: {
            ...sub,
            periodsMap: removeProperty(sub.periodsMap, periodId),
          },
        }),
        {}
      ),
      cgroupsById: Object.entries(state.consolidations.cgroupsById).reduce(
        (prev, [cgroupId, cgroup]) => ({
          ...prev,
          [cgroupId]: {
            ...cgroup,
            cfPeriods: removeProperty(cgroup.cfPeriods, periodId),
          },
        }),
        {}
      ),
    },
    settings,
  };
};

export const editPeriod = (stateAccounts, periodId, periodObj) => {
  return {
    ...stateAccounts,
    periodsById: {
      ...stateAccounts.periodsById,
      [periodId]: {
        ...stateAccounts.periodsById[periodId],
        ...periodObj,
      },
    },
  };
};

export const movePeriod = (stateAccounts, oldIndex, newIndex) => {
  const movedItem = stateAccounts.periodIds[oldIndex];
  const periodIds = stateAccounts.periodIds.filter((p, id) => id !== oldIndex);
  return {
    ...stateAccounts,
    periodIds: [
      ...periodIds.slice(0, newIndex),
      movedItem,
      ...periodIds.slice(newIndex),
    ],
  };
};

/* ------------------------------ Groups ---------------------------------- */

export const renameGroup = (stateGroups, groupId, newName) => {
  return {
    ...stateGroups,
    groupsById: {
      ...stateGroups.groupsById,
      [groupId]: {
        ...stateGroups.groupsById[groupId],
        name: newName,
      },
    },
  };
};

export const editGroupCode = (stateGroups, groupId, newCode) => {
  return {
    ...stateGroups,
    groupsById: {
      ...stateGroups.groupsById,
      [groupId]: {
        ...stateGroups.groupsById[groupId],
        code: newCode,
      },
    },
  };
};

export const editGroupNumber = (stateGroups, groupId, newNumber) => {
  return {
    ...stateGroups,
    groupsById: {
      ...stateGroups.groupsById,
      [groupId]: {
        ...stateGroups.groupsById[groupId],
        number: newNumber,
      },
    },
  };
};

export const setGroupsExpanded = (
  stateGroups,
  groupIds,
  newExpanded,
  shouldSyncExpandedStates = false
) => {
  return {
    ...stateGroups,
    shouldSyncExpandedStates,
    groupsById: Object.entries(stateGroups.groupsById).reduce(
      (groupsById, [groupId, group]) => ({
        ...groupsById,
        [groupId]: {
          ...group,
          expanded: groupIds.includes(parseInt(groupId, 10))
            ? newExpanded
            : group.expanded,
        },
      }),
      {}
    ),
  };
};

export const setShouldSyncExpandedStates = (
  stateGroups,
  shouldSyncExpandedStates
) => {
  return {
    ...stateGroups,
    shouldSyncExpandedStates,
  };
};

/*
 * Adds new group as a child of group with `parentGroupId`.
 */
export const addGroup = (stateGroups, parentGroupId) => {
  let groupId = stateGroups.groupIdMax + 1;
  return {
    ...stateGroups,
    groupIdMax: groupId,
    groupsById: {
      ...stateGroups.groupsById,
      [parentGroupId]: {
        ...stateGroups.groupsById[parentGroupId],
        childrenIds: [
          ...stateGroups.groupsById[parentGroupId].childrenIds,
          groupId,
        ],
      },
      [groupId]: {
        name: `New Group ${groupId}`,
        childrenIds: [],
        code: '',
        number: '',
        cgroupId: null,
        expanded: true,
      },
    },
  };
};

/*
 * Moves group with `groupId` to group with `targetGroupId`.
 */
export const moveGroup = (stateGroups, groupId, targetGroupId) => {
  // do not move if group is root or ancestor or child of target group
  if (
    groupId === 0 ||
    isAncestor(stateGroups, targetGroupId, groupId) ||
    isParent(stateGroups, targetGroupId, groupId)
  ) {
    return stateGroups;
  }
  const parentGroupId = getParent(stateGroups.groupsById, groupId);
  return {
    ...stateGroups,
    groupsById: {
      ...stateGroups.groupsById,
      [parentGroupId]: {
        ...stateGroups.groupsById[parentGroupId],
        childrenIds: stateGroups.groupsById[parentGroupId].childrenIds.filter(
          (gId) => gId !== groupId
        ),
      },
      [targetGroupId]: {
        ...stateGroups.groupsById[targetGroupId],
        childrenIds: [
          ...stateGroups.groupsById[targetGroupId].childrenIds,
          groupId,
        ],
      },
    },
  };
};

export const sortGroups = (
  stateGroups,
  groupId,
  field,
  sortingOrder,
  rows,
  cols
) => {
  const { groupsById } = stateGroups;
  const groupsFromRowsById = {};

  const groupsFromRows = rows.filter((row) => row.group);
  groupsFromRows.forEach((groupFromRow) => {
    groupsFromRowsById[groupFromRow.id] = groupFromRow;
  });

  // Find Value Getter for a 'field', if there is any
  let colWithFieldValueGetter = null;
  cols.forEach((col) => {
    if (col.field === field && col.valueGetter) {
      colWithFieldValueGetter = col.valueGetter;
    }
    if (col.children && col.children.length) {
      col.children.forEach((child) => {
        if (child.field === field && child.valueGetter) {
          colWithFieldValueGetter = child.valueGetter;
        }
      });
    }
  });

  const group = groupsById[groupId];
  group.childrenIds.sort((groupIdA, groupIdB) => {
    const groupFromRowA = groupsFromRowsById[groupIdA];
    const groupFromRowB = groupsFromRowsById[groupIdB];

    let difference = 0;
    // Fields which already exist in groupRows, such as 'unadjusted'
    if (field in groupFromRowA && field in groupFromRowB) {
      difference = groupFromRowA[field] - groupFromRowB[field];
    }
    // Fields which are calculated by valueGetters, such as 'net' and 'grand total', and are not
    // available in groupRows
    else if (colWithFieldValueGetter) {
      difference =
        colWithFieldValueGetter({ data: groupFromRowA }) -
        colWithFieldValueGetter({ data: groupFromRowB });
    }

    const sign = sortingOrder === SortingOrder.ASC ? 1 : -1;
    return sign * difference;
  });

  return {
    ...stateGroups,
    groupsById: {
      ...stateGroups.groupsById,
      [groupId]: group,
    },
  };
};

/*
 * Removes group with `groupId` and all its descendants and related cgroups,
 * unassignes all accounts assigned to those groups and
 * remove all journal entryItems with this groups and unpost entries.
 */
export const removeGroup = (state, groupId) => {
  // find parent
  let parentGroupId = getParent(state.groups.groupsById, groupId);
  let groupsToRemove = new Set();
  let cgroupsToRemove = new Set();
  let stack = [groupId];
  while (stack.length > 0) {
    const gId = stack.pop();
    const group = state.groups.groupsById[gId];
    groupsToRemove.add(gId);
    if (group.cgroupId !== null) {
      cgroupsToRemove.add(group.cgroupId);
    }
    stack.push(...group.childrenIds);
  }
  let accountsToUnassign = Object.entries(
    state.accounts.accountsById
  ).filter(([aId, a]) => groupsToRemove.has(a.groupId));

  let entryItemIdsToRemove = new Set(
    Object.entries(state.journal.entryItemsById)
      .filter(
        ([itemId, item]) =>
          item.contentType === 'group' && groupsToRemove.has(item.contentId)
      )
      .map(([itemId, item]) => itemId)
  );
  let journal = removeJournalEntryItems(state.journal, entryItemIdsToRemove);
  return {
    ...state,
    groups: {
      ...state.groups,
      groupsById: Object.keys(state.groups.groupsById).reduce((acc, gId) => {
        if (parseInt(gId, 10) === parentGroupId) {
          // remove groupId from state.groups.groupsById[parentGroupId].childrenIds
          return {
            ...acc,
            [gId]: {
              ...state.groups.groupsById[gId],
              childrenIds: state.groups.groupsById[gId].childrenIds.filter(
                (id) => id !== groupId
              ),
            },
          };
        } else if (gId === '0' && parentGroupId === -1) {
          // if deleting root (parentGroupId is -1) then empty its childrenIds
          return {
            ...acc,
            [gId]: {
              ...state.groups.groupsById[gId],
              childrenIds: [],
            },
          };
        } else if (!groupsToRemove.has(parseInt(gId, 10))) {
          return { ...acc, [gId]: state.groups.groupsById[gId] };
        }
        return acc;
      }, {}),
    },
    // unassign accountsToUnassign
    accounts: {
      ...state.accounts,
      accountsById: {
        ...state.accounts.accountsById,
        ...arrToObj(
          accountsToUnassign.map(([aId, a]) => {
            return {
              [aId]: {
                ...a,
                groupId: null,
              },
            };
          })
        ),
      },
    },
    // remove all cgroupsToRemove
    consolidations: {
      ...state.consolidations,
      cgroupsById: Object.keys(state.consolidations.cgroupsById).reduce(
        (acc, gId) => {
          if (!cgroupsToRemove.has(parseInt(gId, 10))) {
            return { ...acc, [gId]: state.consolidations.cgroupsById[gId] };
          }
          return acc;
        },
        {}
      ),
    },
    journal,
  };
};

/*
 * Moves group to the given index in its parent children groups array.
 */
export const moveGroupToIndex = (stateGroups, groupId, index) => {
  let parentGroupId = getParent(stateGroups.groupsById, groupId);
  if (parentGroupId === -1) return stateGroups;
  // remove groupId
  let childrenIds = stateGroups.groupsById[parentGroupId].childrenIds.filter(
    (id) => id !== groupId
  );
  // add groupId back to designated index
  return {
    ...stateGroups,
    groupsById: {
      ...stateGroups.groupsById,
      [parentGroupId]: {
        ...stateGroups.groupsById[parentGroupId],
        childrenIds: [
          ...childrenIds.slice(0, index),
          groupId,
          ...childrenIds.slice(index),
        ],
      },
    },
  };
};

export const moveGroupDirection = (stateGroups, groupId, dir) => {
  let parentGroupId = getParent(stateGroups.groupsById, groupId);
  let index = stateGroups.groupsById[parentGroupId].childrenIds.indexOf(
    groupId
  );
  let len = stateGroups.groupsById[parentGroupId].childrenIds.length;
  return moveGroupToIndex(
    stateGroups,
    groupId,
    Math.max(0, Math.min(len, index + dir))
  );
};

export const moveGroupUp = (stateGroups, groupId) => {
  return moveGroupDirection(stateGroups, groupId, -1);
};

export const moveGroupDown = (stateGroups, groupId) => {
  return moveGroupDirection(stateGroups, groupId, 1);
};

export const importGroups = (state, data, mapping) => {
  // Remove all groups
  let newState = removeGroup(state, 0);

  // Bulk create all groups in groups object
  let groups = {
    ...newState.groups,
    groupsById: {
      ...newState.groups.groupsById,
    },
  };

  const mapGroupCodeToGroupId = {};

  // Create all groups from Excel data
  data.forEach((group) => {
    const groupName =
      mapping.groups < group.length
        ? // handle groupName undefined and number (convert to string)
          (
            (group[mapping.groups] && String(group[mapping.groups])) ||
            ''
          ).trim()
        : '';
    const groupCode = mapping.code < group.length ? group[mapping.code] : '';
    const groupNumber =
      mapping.number < group.length ? group[mapping.number] : '';

    if (!groupCode) {
      return;
    }

    const groupId = ++groups.groupIdMax;
    groups.groupsById[groupId] = {
      name: groupName,
      childrenIds: [],
      code: groupCode,
      number: groupNumber,
      cgroupId: null,
      expanded: true,
    };

    mapGroupCodeToGroupId[groupCode] = groupId;
  });

  // Assign groups to other groups according to parentCode
  data.forEach((group) => {
    const groupCode = mapping.code < group.length ? group[mapping.code] : '';
    const groupParentCode =
      mapping.parentCode < group.length ? group[mapping.parentCode] : '';

    if (!groupCode || !groups.groupsById) {
      return;
    }

    const parentGroupId = groupParentCode
      ? mapGroupCodeToGroupId[groupParentCode]
      : 0;
    if (parentGroupId in groups.groupsById) {
      groups.groupsById[parentGroupId].childrenIds.push(
        mapGroupCodeToGroupId[groupCode]
      );
    }
  });

  return {
    ...newState,
    groups,
  };
};

export const leadScheduleReportSaveSelectedGroups = (
  stateGroups,
  savedGroupsIds
) => {
  return {
    ...stateGroups,
    savedGroupsIds,
  };
};

/*
 * Returns true if `groupA` is ancestor of `groupB`.
 */
const isParent = (stateGroups, groupAId, groupBId) => {
  return (
    stateGroups.groupsById[groupAId].childrenIds.indexOf(
      parseInt(groupBId, 10)
    ) !== -1
  );
};

/*
 * Returns id of group with `groupId` or -1 for root group.
 */
const getParent = (groupsById, groupId) => {
  const groupIdInt = parseInt(groupId, 10);
  if (groupId === 0) return -1;
  let id = Object.entries(groupsById).filter(
    ([gId, g]) => g.childrenIds.indexOf(groupIdInt) !== -1
  )[0][0];
  return parseInt(id, 10);
};

/*
 * Returns true if `groupA` is ancestor of `groupB`.
 */
const isAncestor = (stateGroups, groupAId, groupBId) => {
  let currentGroupId = groupBId;
  let queue = [currentGroupId];
  while (queue.length > 0) {
    currentGroupId = queue.shift();
    if (currentGroupId === groupAId) return true;
    queue.push(...stateGroups.groupsById[currentGroupId].childrenIds);
  }
  return false;
};

/* ------------------------------ KPI --------------------------------- */
export const addUpdateKpiEntry = (stateKpi, kpiEntry) => {
  const { id: kpiEntryId, groupId: kpiEntryGroupId } = kpiEntry;

  const newEntriesById = {
    ...stateKpi.entriesById,
  };

  const newGroupsById = {
    ...stateKpi.groupsById,
  };

  const newEntry = {
    ...omit(kpiEntry, ['id', 'contentDescription', 'groupId']),
  };

  let newEntryIdMax =
    kpiEntryId < 0 ? stateKpi.entryIdMax + 1 : stateKpi.entryIdMax;
  const newKpiEntryId = kpiEntryId < 0 ? stateKpi.entryIdMax + 1 : kpiEntryId;
  newEntriesById[newKpiEntryId] = newEntry;

  const kpiEntryPreviousGroupId = findParentGroupIdOfKpiEntry(
    newGroupsById,
    newKpiEntryId
  );

  if (kpiEntryPreviousGroupId === null) {
    const parentGroup = newGroupsById[kpiEntryGroupId];
    addKpiEntryToKpiGroup(parentGroup, newKpiEntryId);
  } else if (kpiEntryPreviousGroupId !== kpiEntryGroupId) {
    const previousParentGroup = newGroupsById[kpiEntryPreviousGroupId];
    removeKpiEntryFromKpiGroup(previousParentGroup, newKpiEntryId);

    const newParentGroup = newGroupsById[kpiEntryGroupId];
    addKpiEntryToKpiGroup(newParentGroup, newKpiEntryId);
  }

  return {
    ...stateKpi,
    groupsById: newGroupsById,
    entryIdMax: newEntryIdMax,
    entriesById: newEntriesById,
  };
};

export const removeKpiEntry = (stateKpi, kpiId) => {
  const newGroupsById = {
    ...stateKpi.groupsById,
  };

  const newEntriesById = {
    ...omit(stateKpi.entriesById, kpiId),
  };

  const kpiEntryParentGroupId = findParentGroupIdOfKpiEntry(
    stateKpi.groupsById,
    kpiId
  );

  if (kpiEntryParentGroupId !== null) {
    const parentGroup = newGroupsById[kpiEntryParentGroupId];
    removeKpiEntryFromKpiGroup(parentGroup, kpiId);
  }

  return {
    ...stateKpi,
    groupsById: newGroupsById,
    entriesById: newEntriesById,
  };
};

export function findParentGroupIdOfKpiEntry(groupsById, kpiEntryId) {
  const parentGroupId = Object.keys(groupsById).find((gId) => {
    const group = groupsById[gId];
    return group.childrenIds.includes(kpiEntryId);
  });

  return !!parentGroupId ? parentGroupId : null;
}

function addKpiEntryToKpiGroup(kpiGroup, kpiEntryId) {
  kpiGroup.childrenIds.push(kpiEntryId);
}

function removeKpiEntryFromKpiGroup(kpiGroup, kpiEntryId) {
  kpiGroup.childrenIds = kpiGroup.childrenIds.filter(
    (childId) => childId !== kpiEntryId
  );
}

/*
 * Adds new KPI group.
 */
export const addKpiGroup = (stateKpi) => {
  const newGroupId = stateKpi.groupIdMax + 1;
  const newGroupIds = stateKpi.groupIds.concat([newGroupId]);

  return {
    ...stateKpi,
    groupIdMax: newGroupId,
    groupIds: newGroupIds,
    groupsById: {
      ...stateKpi.groupsById,
      [newGroupId]: {
        name: `New KPI Group ${newGroupId}`,
        childrenIds: [],
        expanded: true,
      },
    },
  };
};

/*
 * Removes KPI group defined by `groupId` and all KPIs associated with it.
 */
export const removeKpiGroup = (stateKpi, groupId) => {
  let newEntriesById = {
    ...stateKpi.entriesById,
  };

  const groupChildrenIds = stateKpi.groupsById[groupId].childrenIds;
  groupChildrenIds.forEach((childId) => {
    newEntriesById = omit(newEntriesById, childId);
  });

  const newGroupIds = stateKpi.groupIds.filter((id) => id !== groupId);
  const newGroupsById = omit(stateKpi.groupsById, groupId);

  return {
    ...stateKpi,
    groupIds: newGroupIds,
    groupsById: newGroupsById,
    entriesById: newEntriesById,
  };
};

export const editKpiGroup = (stateKpi, group) => {
  return {
    ...stateKpi,
    groupsById: {
      ...stateKpi.groupsById,
      ...group,
    },
  };
};

export const moveKpiGroupUp = (stateKpi, groupId) => {
  const newGroupIds = [...stateKpi.groupIds];
  moveElementInArray(newGroupIds, groupId, -1);

  return {
    ...stateKpi,
    groupIds: newGroupIds,
  };
};

export const moveKpiGroupDown = (stateKpi, groupId) => {
  const newGroupIds = [...stateKpi.groupIds];
  moveElementInArray(newGroupIds, groupId, 1);

  return {
    ...stateKpi,
    groupIds: newGroupIds,
  };
};

export const moveKpiUp = (stateKpi, kpiId) => {
  return moveKpi(stateKpi, kpiId, -1);
};

export const moveKpiDown = (stateKpi, kpiId) => {
  return moveKpi(stateKpi, kpiId, 1);
};

export const moveKpi = (stateKpi, kpiId, delta) => {
  const newGroupsById = {
    ...stateKpi.groupsById,
  };
  const parentGroupId = findParentGroupIdOfKpiEntry(newGroupsById, kpiId);
  const parentGroup = newGroupsById[parentGroupId];

  moveElementInArray(parentGroup.childrenIds, kpiId, delta);

  return {
    ...stateKpi,
    groupsById: newGroupsById,
  };
};

export const moveElementInArray = (arr, element, delta) => {
  const elementIndex = arr.indexOf(element);
  const newIndex = elementIndex + delta;

  const isElementOnTopOrBottom = newIndex < 0 || newIndex === arr.length;
  if (isElementOnTopOrBottom) {
    return;
  }

  arr.splice(elementIndex, 1);
  arr.splice(newIndex, 0, element);
};

/* ------------------------------ Settings -------------------------------- */
export const saveSettings = (stateSettings, settings) => {
  return {
    ...stateSettings,
    ...settings,
  };
};

export const DEFAULT_EXCHANGE_RATE = 1;

export const addExchangeRate = (state) => {
  const index = (state.settings?.exchangeRates?.length || 0) + 1;
  const exchangeRateName = `Exchange rate ${index}`;
  const periodIds = state.accounts.periodIds || [];
  const periodValues = {};

  periodIds.forEach((periodId) => {
    periodValues[`er-${periodId}`] = DEFAULT_EXCHANGE_RATE;
  });

  return {
    ...state,
    settings: {
      ...state.settings,
      exchangeRates: [
        ...(state?.settings?.exchangeRates || []),
        {
          id: uuid(),
          name: exchangeRateName,
          ...periodValues,
        },
      ],
    },
  };
};

export const editExchangeRate = (state, exchangeRate) => {
  const exchangeRates = state?.settings?.exchangeRates || [];
  const exchangeRateIndex = exchangeRates.findIndex(
    (er) => er.id === exchangeRate.id
  );

  if (exchangeRateIndex !== -1) {
    exchangeRates[exchangeRateIndex] = exchangeRate;
  }

  return {
    ...state,
    settings: {
      ...state.settings,
      exchangeRates,
    },
  };
};

export const removeExchangeRate = (state, exchangeRateId) => {
  const exchangeRates = (state?.settings?.exchangeRates || []).filter(
    (exchangeRate) => exchangeRate.id !== exchangeRateId
  );

  const groupsById = Object.entries(state.groups.groupsById).reduce(
    (prev, [groupId, group]) => ({
      ...prev,
      [groupId]: {
        ...group,
        exchangeRateId:
          group.exchangeRateId === exchangeRateId ? '' : group.exchangeRateId,
      },
    }),
    {}
  );

  const accountsById = Object.entries(state.accounts.accountsById).reduce(
    (prev, [accountId, account]) => ({
      ...prev,
      [accountId]: {
        ...account,
        exchangeRateId:
          account.exchangeRateId === exchangeRateId
            ? ''
            : account.exchangeRateId,
      },
    }),
    {}
  );

  return {
    ...state,
    groups: {
      ...state.groups,
      groupsById,
    },
    accounts: {
      ...state.accounts,
      accountsById,
    },
    settings: {
      ...state.settings,
      exchangeRates,
    },
  };
};

export const refreshExchangeRates = (state) => {
  let newState = state;

  const queue = [{ id: '0', type: 'group' }];
  const traversal = [];

  while (queue.length > 0) {
    const currentItem = queue.shift();

    const isGroup = currentItem.type === 'group';

    const currentGroup = isGroup && state.groups.groupsById[currentItem.id];

    if (currentGroup) {
      traversal.push({ ...currentGroup, id: currentItem.id });

      const groupChildrenItems = currentGroup.childrenIds.map((childId) => {
        return {
          id: childId,
          type: 'group',
        };
      });

      const accountChildrenitems = Object.values(state.accounts.accountsById)
        .filter((account) => account.groupId === currentItem.id)
        .map((childId) => {
          return {
            id: childId,
            type: 'account',
          };
        });

      queue.push(...groupChildrenItems, ...accountChildrenitems);
    }
  }

  reverse(traversal);

  const onGroup = (newExchangeRateId) => (groupId) => {
    newState = {
      ...newState,
      groups: {
        ...newState.groups,
        groupsById: {
          ...newState.groups.groupsById,
          [groupId]: {
            ...newState.groups.groupsById[groupId],
            exchangeRateId: newExchangeRateId,
          },
        },
      },
    };
  };

  traversal.forEach((group) => {
    const groupId = group.id;
    const childrenGroupIds = group.childrenIds;
    const childrenAccountIds = newState.accounts.accountIds.filter(
      (accountId) =>
        newState.accounts.accountsById[accountId].groupId === groupId
    );

    const childrenGroups = childrenGroupIds.map(
      (groupId) => newState.groups.groupsById[groupId]
    );
    const childrenAccounts = childrenAccountIds.map(
      (accountId) => newState.accounts.accountsById[accountId]
    );

    const children = [...childrenGroups, ...childrenAccounts];

    const firstChild = children[0];

    if (!firstChild) {
      onGroup(group.exchangeRateId)(groupId);

      return;
    }

    const firstChildExchangeRate = firstChild.exchangeRateId || '';

    const MIXED_EXCHANGE_RATE = 'MIXED_EXCHANGE_RATE';

    const isSameExchangeRate = children.every((child) => {
      return (child.exchangeRateId || '') === firstChildExchangeRate;
    });

    const finalExchangeRate = isSameExchangeRate
      ? firstChildExchangeRate
      : MIXED_EXCHANGE_RATE;

    onGroup(finalExchangeRate)(groupId);
  });

  return newState;
};

export const saveExchangeRate = (state, itemId, itemType, exchangeRateId) => {
  if (itemType === 'account') {
    return {
      ...state,
      accounts: {
        ...state.accounts,
        accountsById: {
          ...state.accounts.accountsById,
          [itemId]: {
            ...state.accounts.accountsById[itemId],
            exchangeRateId,
          },
        },
      },
    };
  }

  let newState = { ...state };

  const groupIdsToUpdate = new Set();
  const accountIdsToUpdate = new Set();

  const traverseAndUpdate = (currentId, currentType) => {
    const isGroup = currentType === 'group';
    const currentGroup = isGroup && newState.groups.groupsById[currentId];

    if (currentGroup) {
      groupIdsToUpdate.add(currentId);

      currentGroup.childrenIds.forEach((childId) => {
        traverseAndUpdate(childId, 'group');
      });

      newState.accounts.accountIds
        .filter(
          (accountId) =>
            newState.accounts.accountsById[accountId].groupId === currentId
        )
        .forEach((accountId) => {
          accountIdsToUpdate.add(accountId);
        });
    }
  };

  traverseAndUpdate(itemId, itemType);

  const updateGroups = () => {
    let newGroupsById = newState.groups.groupsById;

    groupIdsToUpdate.forEach((groupId) => {
      newGroupsById = {
        ...newGroupsById,
        [groupId]: { ...newGroupsById[groupId], exchangeRateId },
      };
    });

    newState = {
      ...newState,
      groups: {
        ...newState.groups,
        groupsById: {
          ...newGroupsById,
        },
      },
    };
  };

  const updateAccounts = () => {
    let newAccountsById = newState.accounts.accountsById;

    accountIdsToUpdate.forEach((accountId) => {
      newAccountsById = {
        ...newAccountsById,
        [accountId]: { ...newAccountsById[accountId], exchangeRateId },
      };
    });

    newState = {
      ...newState,
      accounts: {
        ...newState.accounts,
        accountsById: {
          ...newAccountsById,
        },
      },
    };
  };

  updateGroups();
  updateAccounts();

  return newState;
};

/* ------------------------------ Journal --------------------------------- */

/*
 * Updates existing or adds new journal entry. If entry id or entry
 * item id is negative then it is added otherwise updated.
 * journalEntry structure is documented in selectors/journals
 */
export const addUpdateJournalEntry = (stateJournal, journalEntry) => {
  let { id, periodId, type, description, status, items, er } = journalEntry;
  let newEntry = {
    periodId,
    type,
    description,
    status,
    er,
    modified: new Date(),
  };
  let entriesById = {
    ...stateJournal.entriesById,
  };
  if (id < 0) {
    id = getAvailableIds(entriesById, 1)[0];
  }
  entriesById[id] = newEntry;

  // delete removed items:
  // * preserve stateJournal entry items that are part of other entries or
  // * still in items i.e. not removed by this update
  let entryItemsById = filterProperties(
    stateJournal.entryItemsById,
    (itemId, item) => item.entryId !== id || items.hasOwnProperty(itemId)
  );

  let newItems = Object.entries(items).filter(([itemId, item]) => itemId < 0);
  let newItemIds = getAvailableIds(entryItemsById, newItems.length);

  let i = 0;
  for (let [itemId, item] of Object.entries(items)) {
    item.entryId = id;
    delete item.contentDescription;
    if (itemId < 0) {
      itemId = newItemIds[i];
      i += 1;
    }
    entryItemsById[itemId] = item;
  }

  return {
    ...stateJournal,
    entriesById,
    entryItemsById,
  };
};

/*
 * Removes all journal entry items in entryItemIds set and unposts all
 * related entries.
 */
export const removeJournalEntryItems = (stateJournal, entryItemIds) => {
  let entriesToUnpost = [...entryItemIds].map(
    (itemId) => stateJournal.entryItemsById[itemId].entryId
  );
  return {
    ...stateJournal,
    // unpost
    entriesById: {
      ...stateJournal.entriesById,
      ...entriesToUnpost.reduce((acc, eId) => {
        return {
          ...acc,
          [eId]: {
            ...stateJournal.entriesById[eId],
            status: 'unposted',
          },
        };
      }, {}),
    },
    // remove
    entryItemsById: filterProperties(
      stateJournal.entryItemsById,
      (itemId, item) => !entryItemIds.has(itemId)
    ),
  };
};

/*
 * Removes all journal entries in entryIds array and related entry items.
 */
export const removeJournalEntries = (stateJournal, entryIds) => {
  let entriesById = filterProperties(
    stateJournal.entriesById,
    (id, entry) => !entryIds.has(parseInt(id, 10))
  );
  let entryItemsById = filterProperties(
    stateJournal.entryItemsById,
    (id, entryItem) => !entryIds.has(entryItem.entryId)
  );
  return {
    ...stateJournal,
    entriesById,
    entryItemsById,
  };
};

export const setJournalStatus = (stateJournal, entryId, posted) => {
  return {
    ...stateJournal,
    entriesById: {
      ...stateJournal.entriesById,
      [entryId]: {
        ...stateJournal.entriesById[entryId],
        status: posted ? 'posted' : 'unposted',
      },
    },
  };
};

/*
 * contentType = 'account' or 'group'
 * entryTypes = 'aje', 'cje' or 'rje'
 *
 * Calculates the 'adjusted' key for each period object with posted journal entry item within
 * journal entry with given entryTypes.
 */

export const getAdjustedContentById = (contentType, entryTypes) => (
  contentById,
  journalEntriesById,
  journalEntryItems
) => {
  const adjustedContentById = {};

  for (const entryItem of journalEntryItems) {
    const entry = journalEntriesById[entryItem.entryId];
    if (
      entryItem.contentType !== contentType ||
      entry.status !== 'posted' ||
      !entryTypes.includes(entry.type)
    ) {
      continue;
    }

    const contentId = entryItem.contentId;
    const content = contentById[contentId];
    const entryEr = !!entry.er;
    const adjustedKey = entryEr ? 'er-adjusted' : 'adjusted';

    const entryItemValue = entryItem.debit.subtract(entryItem.credit);
    const hasContent = adjustedContentById.hasOwnProperty(contentId);
    const hasPeriod =
      hasContent &&
      adjustedContentById[contentId].periods.hasOwnProperty(entry.periodId);
    let adjustedValue = hasPeriod
      ? adjustedContentById[contentId].periods[entry.periodId][adjustedKey]
      : currency(0);
    adjustedValue = currency(adjustedValue).add(entryItemValue);

    if (!content.periods) {
      adjustedContentById[contentId] = {
        ...(contentId in adjustedContentById
          ? adjustedContentById[contentId]
          : content),
        periods: {
          ...(contentId in adjustedContentById
            ? adjustedContentById[contentId].periods
            : {}),
          [entry.periodId]: {
            [adjustedKey]: adjustedValue,
          },
        },
      };
    } else {
      adjustedContentById[contentId] = {
        ...(contentId in adjustedContentById
          ? adjustedContentById[contentId]
          : content),
        periods: {
          ...content.periods,
          ...(contentId in adjustedContentById
            ? adjustedContentById[contentId].periods
            : {}),
          [entry.periodId]: {
            ...content.periods[entry.periodId],
            ...{
              adjusted:
                adjustedContentById?.[contentId]?.periods?.[entry?.periodId]?.[
                  'adjusted'
                ] || undefined,
              // eslint-disable-next-line
              ['er-adjusted']:
                adjustedContentById?.[contentId]?.periods?.[entry?.periodId]?.[
                  'er-adjusted'
                ] || undefined,
            },
            [adjustedKey]: adjustedValue,
          },
        },
      };
    }
  }

  return {
    ...contentById,
    ...adjustedContentById,
  };
};

/* --------------------------- Consolidations ------------------------------ */

/*
 * Dictionary:
 * group object - object in state that can represent either:
 *   group - regular group, with cgroupId===null or
 *   subgroup - group imported from subsidiary, with cgroupId!==null
 * cgroup object - object in state that has additional data related to subgroup
 * cfgroup - object in cfObj that represents regular group in subsidiary
 *           client file
 */

/*
 * For each cfgroup in cfgroupsPeriods create group and cgroup object.
 */
const addCfgroups = (state, cfgroupsPeriods, cfObj, subId) => {
  // assert subId is in the state
  if (!state.consolidations.subsidiariesById.hasOwnProperty(subId)) {
    return state;
  }

  let newCgroups = {};
  let newGroups = {};
  let changedGroups = {};
  let cgroupIds = getAvailableIds(
    state.consolidations.cgroupsById,
    cfgroupsPeriods.size
  );
  // reverse for efficient pop
  cgroupIds.reverse();
  let groupId = state.groups.groupIdMax;
  for (const [cfgroupId, cfgroupPeriods] of cfgroupsPeriods) {
    // create cgroup
    const cgroupId = cgroupIds.pop();
    newCgroups[cgroupId] = {
      subId: subId,
      cfGroupId: cfgroupId,
      cfPeriods: {
        ...cfgroupPeriods,
      },
    };

    // create group
    const code = cfObj.groups.groupsById[cfgroupId].code;
    newGroups[++groupId] = {
      name: cfObj.groups.groupsById[cfgroupId].name,
      childrenIds: [],
      // Remove code from created group to avoid duplicate codes
      code: '',
      number: '',
      cgroupId: cgroupId,
      expanded: true,
    };

    // add new group as a child to the group with same unique code
    // (unique code is already asserted using groupCodesUnique)
    // otherwise add it to the group 0
    let parentGroupId = 0;
    const groupIdByCode = new Map(
      Object.entries(state.groups.groupsById).map(([gId, g]) => [g.code, gId])
    );
    if (code !== '' && groupIdByCode.has(code)) {
      parentGroupId = groupIdByCode.get(code);
    }
    // on first change of parent group move it to changedGroups
    if (!changedGroups.hasOwnProperty(parentGroupId)) {
      changedGroups[parentGroupId] = {
        ...state.groups.groupsById[parentGroupId],
      };
    }
    // add groupId to parent group's childrenIds
    changedGroups[parentGroupId].childrenIds = [
      ...changedGroups[parentGroupId].childrenIds,
      groupId,
    ];
  }

  return {
    ...state,
    consolidations: {
      ...state.consolidations,
      cgroupsById: {
        ...state.consolidations.cgroupsById,
        ...newCgroups,
      },
    },
    groups: {
      ...state.groups,
      groupIdMax: groupId,
      groupsById: {
        ...state.groups.groupsById,
        ...changedGroups,
        ...newGroups,
      },
    },
  };
};

/*
 * Find all groups in cfObj that have at least one mapped period with grand
 * total (sum only directly mapped accounts and subgroups) not equal to zero.
 * Returns map (GroupId: {period0: p0Balance, period1: p1Balance})
 */
// TODO: Here
const getCfGroupsPeriods = (sub, cfObj) => {
  // use only mapped periods
  const mappedCfPeriodIds = [...Object.values(sub.periodsMap)].filter(
    (periodId) => periodId !== null
  );

  // use selector to sum adjusted groups
  const adjustedGroupsById = getAdjustedContentById('group', [
    'aje',
    'rje',
    'cje',
  ])(
    cfObj.groups.groupsById,
    cfObj.journal.entriesById,
    Object.values(cfObj.journal.entryItemsById)
  );

  // use selector to be able to sum adjusted accounts
  const adjustedAccountsById = getAdjustedContentById('account', [
    'aje',
    'rje',
    'cje',
  ])(
    cfObj.accounts.accountsById,
    cfObj.journal.entriesById,
    Object.values(cfObj.journal.entryItemsById)
  );

  const groupIdsWithAccounts = _(
    Object.values(adjustedAccountsById)
      .filter((account) => account.groupId !== null)
      .map((account) => account.groupId.toString())
  )
    .compact()
    .uniq()
    .value();

  // groups tracks groups grand total for mapped cfperiods, skip subgroups
  // This should be only Journal entries, goes through all groups
  const groups = arrToObj(
    Object.keys(adjustedGroupsById)
      .filter((groupId) => cfObj.groups.groupsById[groupId].cgroupId === null)
      .map((groupId) => {
        return {
          [groupId]: arrToObj(
            mappedCfPeriodIds.map((pId) => {
              let value = currency(0);
              try {
                const group = adjustedGroupsById[groupId];
                const exchangeRateId = group.exchangeRateId;
                const exchangeRate = (cfObj.settings?.exchangeRates || []).find(
                  (exchangeRate) => exchangeRate.id === exchangeRateId
                );
                value = group.periods[pId].adjusted;
                const isGroupWithAccount = groupIdsWithAccounts.includes(
                  groupId
                );

                if (!isGroupWithAccount && exchangeRate) {
                  value = convertWithExchangeRate(
                    value,
                    exchangeRate[`er-${pId}`]
                  );
                }
              } catch (e) {
                // if group does not have any adjustment
              }
              return { [pId]: value };
            })
          ),
        };
      })
  );

  // grand total for a group is sum of all directly mapped accounts (unadjusted
  // and adjusted) and directly mapped subgroups, regular child groups excluded
  // This goes through groups which have at least one account

  for (const account of Object.values(adjustedAccountsById)) {
    if (account.groupId === null || !groups.hasOwnProperty(account.groupId))
      continue;

    const group = groups[account.groupId];
    const accountsOfGroup = Object.values(adjustedAccountsById).filter(
      (acc) => acc.groupId === account.groupId
    );

    const groupFromAdjustedGroups = adjustedGroupsById[account.groupId];
    const groupExchangeRateId = groupFromAdjustedGroups.exchangeRateId;
    const areAllAccountsOfSameExchangeRateId = accountsOfGroup.every(
      (acc) => acc.exchangeRateId === groupExchangeRateId
    );

    for (const periodId of mappedCfPeriodIds) {
      if (areAllAccountsOfSameExchangeRateId) {
        group[periodId] = group[periodId].add(
          account.periods[periodId].unadjusted
        );

        group[periodId] = group[periodId].add(
          account.periods[periodId].adjusted
        );
      } else {
        const adjustAccountsToER = (acc, amount, pId) => {
          const exchangeRateId = acc.exchangeRateId;
          const exchangeRate = (cfObj.settings?.exchangeRates || []).find(
            (exchangeRate) => exchangeRate.id === exchangeRateId
          );

          if (exchangeRate) {
            const convertedAmount = convertWithExchangeRate(
              amount,
              exchangeRate[`er-${pId}`]
            );
            return convertedAmount;
          }

          return amount;
        };

        group[periodId] = group[periodId].add(
          adjustAccountsToER(
            account,
            account.periods[periodId].unadjusted,
            periodId
          )
        );
        group[periodId] = group[periodId].add(
          adjustAccountsToER(
            account,
            account.periods[periodId].adjusted,
            periodId
          )
        );
      }
    }
  }

  Object.keys(groups).forEach((groupId) => {
    if (!groupIdsWithAccounts.includes(groupId)) {
      return;
    }

    const group = groups[groupId];
    const groupFromAdjustedGroups = adjustedGroupsById[groupId];
    const groupExchangeRateId = groupFromAdjustedGroups.exchangeRateId;

    const groupExchangeRate = (cfObj.settings?.exchangeRates || []).find(
      (exchangeRate) => exchangeRate.id === groupExchangeRateId
    );

    const accountsOfGroup = Object.values(adjustedAccountsById).filter(
      (acc) => acc.groupId === groupId
    );

    const areAllAccountsOfSameExchangeRateId = accountsOfGroup.every(
      (acc) => acc.exchangeRateId === groupExchangeRateId
    );

    if (!areAllAccountsOfSameExchangeRateId || !groupExchangeRate) {
      return;
    }

    for (const periodId of mappedCfPeriodIds) {
      group[periodId] = convertWithExchangeRate(
        group[periodId],
        groupExchangeRate[`er-${periodId}`]
      );
    }
  });

  // for each adjusted subgroup get its first regular group ancestor
  // and for each mapped period sum their balance and adjustments
  // and add it to this ancestor grand total

  for (const [groupId, adjustedGroup] of Object.entries(adjustedGroupsById)) {
    if (adjustedGroup.cgroupId === null) continue;
    let ancestorGroupId = groupId;
    do {
      ancestorGroupId = getParent(adjustedGroupsById, ancestorGroupId);
    } while (adjustedGroupsById[ancestorGroupId].cgroupId !== null);
    const cgroup = cfObj.consolidations.cgroupsById[adjustedGroup.cgroupId];
    const subsidiary = cfObj.consolidations.subsidiariesById[cgroup.subId];
    for (const periodId of mappedCfPeriodIds) {
      // get subsidiary mapping of periodId
      const subPeriodId = subsidiary.periodsMap[periodId];
      // skip if this period is not mapped in subsidiary
      if (subPeriodId === null) continue;
      const value = cgroup.cfPeriods[subPeriodId];
      groups[ancestorGroupId][periodId] = groups[ancestorGroupId][periodId].add(
        value
      );
    }
  }

  return new Map(
    Object.entries(groups).filter(([cfGroupId, group]) => {
      // skip groups that have 0 balance for all periods
      let all_zeros = true;
      for (const balance of Object.values(group)) {
        all_zeros = all_zeros && balance.value === 0;
      }
      return !all_zeros;
    })
  );
};

export const groupCodesUnique = (stateGroups) => {
  const codes = Object.values(stateGroups.groupsById)
    .map((group) => group.code)
    .filter((code) => code !== '');
  return new Set(codes).size === codes.length;
};

/*
 * Find groupId of the first subgroup from subId with no children.
 */
const findLeafSubgroupId = (
  groupsById,
  cgroupsById,
  subId,
  skipTheseSubgroups = new Map()
) => {
  for (const [groupId, group] of Object.entries(groupsById)) {
    if (group.cgroupId === null) continue;
    if (group.childrenIds.length > 0) continue;
    if (cgroupsById[group.cgroupId].subId !== subId) continue;
    if (skipTheseSubgroups.has(groupId)) continue;
    return parseInt(groupId, 10);
  }
  return null;
};

/*
 * Iteratively remove all leaf subgroups.
 */
const removeLeafSubgroups = (state, subId, skipTheseSubgroups = new Set()) => {
  let newState = state;
  while (true) {
    const groupId = findLeafSubgroupId(
      newState.groups.groupsById,
      newState.consolidations.cgroupsById,
      subId,
      skipTheseSubgroups
    );
    if (groupId === null) break;
    newState = removeGroup(newState, groupId);
  }
  return newState;
};

/*
 * Convert subgroups from subsidiary with subId to groups in state by
 * setting all cgroupId to null and removing related cgroups.
 */
const convertSubgroupsToGroups = (
  state,
  subId,
  skipTheseSubgroups = new Set()
) => {
  let cgroupsToRemove = new Set();
  let newState = state;
  // set group.cgroupId to null
  let groupsById = {};
  const subsidiary = newState.consolidations.subsidiariesById[subId];
  for (const [groupId, group] of Object.entries(newState.groups.groupsById)) {
    let cgroupId = group.cgroupId;
    let name = group.name;
    if (
      cgroupId !== null &&
      newState.consolidations.cgroupsById[cgroupId].subId === subId &&
      !skipTheseSubgroups.has(groupId)
    ) {
      cgroupsToRemove.add(cgroupId);
      cgroupId = null;
      // preserve name of the subsidiary in the new group
      name = `${subsidiary.name} ${name}`;
    }
    groupsById[groupId] = {
      ...group,
      cgroupId,
      name,
    };
  }

  return {
    ...newState,
    groups: {
      ...newState.groups,
      groupsById,
    },
    consolidations: {
      ...newState.consolidations,
      // remove cgroups
      cgroupsById: filterProperties(
        newState.consolidations.cgroupsById,
        (cgroupId, cgroup) => !cgroupsToRemove.has(cgroupId)
      ),
    },
  };
};

/*
 * Creates new subsidiary, group and cgroup objects using data from cfObj.
 * Add new subgroups to existing groups as children, using field 'code' as a
 * key (must be unique).
 *
 * Transfer cfgroups balances in mapped periods to cgroups.
 */
export const addSubsidiary = (state, sub, cfObj) => {
  // group codes must be unique
  if (!groupCodesUnique(state.groups)) return state;

  // get all cfgroupsPeriods and their mapped period balances
  const cfgroupsPeriods = getCfGroupsPeriods(sub, cfObj);

  // add subsidiary object
  let subId = getAvailableIds(state.consolidations.subsidiaryIds, 1)[0];
  let newState = {
    ...state,
    consolidations: {
      ...state.consolidations,
      subsidiaryIds: [...state.consolidations.subsidiaryIds, subId],
      subsidiariesById: {
        ...state.consolidations.subsidiariesById,
        [subId]: sub,
      },
    },
  };

  newState = addCfgroups(newState, cfgroupsPeriods, cfObj, subId);

  return newState;
};

export const updateSubsidiary = (state, subId, sub, cfObj) => {
  // get all cfgroups that should be added or updated in cfgroupsPeriodsToAddUpdate
  const cfgroupsPeriodsToAddUpdate = getCfGroupsPeriods(sub, cfObj);

  // split cfgroupsPeriodsToAddUpdate to cfgroupsPeriodsToAdd and subgroupsToUpdate
  let cfgroupsPeriodsToAdd = new Map(cfgroupsPeriodsToAddUpdate);
  let subgroupsToUpdate = new Set();
  for (const [groupId, group] of Object.entries(state.groups.groupsById)) {
    if (group.cgroupId === null) continue;
    const cgroup = state.consolidations.cgroupsById[group.cgroupId];
    if (cgroup.subId !== subId) continue;
    if (cfgroupsPeriodsToAddUpdate.has(cgroup.cfGroupId)) {
      subgroupsToUpdate.add(groupId);
      cfgroupsPeriodsToAdd.delete(cgroup.cfGroupId);
    }
  }

  let newState = state;

  // remove all leaf subgroups that are not in subgroupsToUpdate
  newState = removeLeafSubgroups(newState, subId, subgroupsToUpdate);

  // convert to groups all subgroups not in subgroupsToUpdate
  newState = convertSubgroupsToGroups(newState, subId, subgroupsToUpdate);

  // for each subgroup in subgroupsToUpdate update name and cfPeriods
  let groupsById = { ...newState.groups.groupsById };
  let cgroupsById = { ...newState.consolidations.cgroupsById };
  for (const groupId of subgroupsToUpdate) {
    const group = groupsById[groupId];
    const cgroup = cgroupsById[group.cgroupId];
    const cfgroupPeriods = cfgroupsPeriodsToAddUpdate.get(cgroup.cfGroupId);
    groupsById[groupId] = {
      ...group,
      name: cfObj.groups.groupsById[cgroup.cfGroupId].name,
    };
    cgroupsById[group.cgroupId] = {
      ...cgroup,
      cfPeriods: cfgroupPeriods,
    };
  }
  newState = {
    ...newState,
    groups: {
      ...newState.groups,
      groupsById,
    },
    consolidations: {
      ...newState.consolidations,
      cgroupsById,
    },
  };

  // for each cfgroup in cfgroupsPeriodsToAdd create new subgroups
  newState = addCfgroups(newState, cfgroupsPeriodsToAdd, cfObj, subId);

  // update subsidiary object
  newState = {
    ...newState,
    consolidations: {
      ...newState.consolidations,
      subsidiariesById: {
        ...newState.consolidations.subsidiariesById,
        [subId]: sub,
      },
    },
  };

  return newState;
};

/*
 * First iteratively remove all subId owned subgroups.
 * There are two options:
 * 1. those subgroups are prevented to have children so delete is clean
 * 2. if those subgroups have children then they cannot be deleted so
 *    they are to be converted to groups
 * Decision was to implement option 2.
 *
 * First delete leaf subgroups.
 * Once when there are no subgroups that can be deleted then all other must be
 * converted to groups.
 */
export const removeSubsidiary = (state, subId) => {
  // remove leaf subgroups
  let newState = removeLeafSubgroups(state, subId);

  // convert remaining subgroups to groups
  newState = convertSubgroupsToGroups(newState, subId);

  // remove subsidiary object
  return {
    ...newState,
    consolidations: {
      ...newState.consolidations,
      subsidiaryIds: newState.consolidations.subsidiaryIds.filter(
        (sId) => sId !== parseInt(subId, 10)
      ),
      subsidiariesById: removeProperty(
        newState.consolidations.subsidiariesById,
        subId
      ),
    },
  };
};

/* --------------------------------- Cf ------------------------------------ */

export const createCfObj = (cfString) => {
  let CfObj = JSON.parse(cfString);
  // convert all numbers to currency
  CfObj = {
    ...CfObj,
    auth: {
      hasLoadedIsLoggedIn: true,
      isLoggedIn: true,
      hasLoadedHasSubscription: true,
      hasSubscription: true,
    },
    accounts: {
      ...CfObj.accounts,
      accountsById: Object.entries(CfObj.accounts.accountsById).reduce(
        (target, [accountId, account]) => {
          return {
            ...target,
            [accountId]: {
              ...account,
              number: account.number && account.number.toString(),
              periods: Object.entries(account.periods).reduce(
                (ptarget, [periodId, period]) => {
                  return {
                    ...ptarget,
                    [periodId]: {
                      ...period,
                      unadjusted: currency(period.unadjusted),
                    },
                  };
                },
                {}
              ),
            },
          };
        },
        {}
      ),
    },
    journal: {
      ...CfObj.journal,
      entryItemsById: Object.entries(CfObj.journal.entryItemsById).reduce(
        (target, [entryItemId, entryItem]) => {
          return {
            ...target,
            [entryItemId]: {
              ...entryItem,
              credit: currency(entryItem.credit),
              debit: currency(entryItem.debit),
            },
          };
        },
        {}
      ),
    },
    consolidations: {
      ...CfObj.consolidations,
      cgroupsById: Object.entries(CfObj.consolidations.cgroupsById).reduce(
        (target, [cgroupId, cgroup]) => {
          return {
            ...target,
            [cgroupId]: {
              ...cgroup,
              cfPeriods: Object.entries(cgroup.cfPeriods).reduce(
                (ptarget, [periodId, periodBalance]) => {
                  return {
                    ...ptarget,
                    [periodId]: currency(periodBalance),
                  };
                },
                {}
              ),
            },
          };
        },
        {}
      ),
    },
    kpi: {
      entryIdMax: 0,
      entriesById: {},
      groupIds: [],
      groupIdMax: 0,
      groupsById: {},
      ...(CfObj.kpi ? CfObj.kpi : {}),
    },
    settings: {
      decimalSeparator: ',',
      thousandsSeparator: '.',
      groupsPrecision: -1,
      precision: 2,
      ...(CfObj.settings ? CfObj.settings : {}),
    },
    previousState: {},
  };
  return CfObj;
};

/* ------------------------------- Generic --------------------------------- */

/*
 * For given object obj or array returns first n available keys with
 * prefix+number format using pigeonhole principle.
 */
const getAvailableIds = (obj, n, prefix = '') => {
  if (Array.isArray(obj)) {
    obj = obj.reduce((o, key) => ({ ...o, [key]: 1 }), {});
  }
  let length = Object.keys(obj).length;
  return [...Array(length + n).keys()]
    .map((key) => (prefix === '' ? parseInt(key, 10) : `${prefix}${key}`))
    .filter((key) => !obj[key])
    .splice(0, n);
};
